// src/context/AuthContext.jsx
// Zentraler Auth- und Dark-Mode-Context
// Verhindert redundante supabase.auth.getUser()-Anfragen in jeder Komponente

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { istMeisterTeam, istSpielleitung, istSuperAdmin, resolveUserRole } from '../utils/roles';
import { leseThema, naechstesThema, setzeThema, type Thema } from '../lib/thema';

const AuthContext = createContext(null);

/**
 * AuthProvider – wrapp die gesamte App und stellt Auth-State global bereit.
 * Komponenten können über useAuth() darauf zugreifen.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  // Ohne konfiguriertes Supabase gibt es nichts zu laden – das steht schon
  // beim ersten Rendern fest und braucht keinen Effekt.
  const [loading, setLoading] = useState(Boolean(supabase));
  const [darkMode, setDarkMode] = useState(() => {
    // Initialwert aus localStorage lesen, damit kein Flash beim Laden entsteht
    return typeof window !== 'undefined' && localStorage.getItem('theme') === 'dark';
  });

  // ── Dark Mode ──────────────────────────────────────────────────────────────
  // Body- und HTML-Klasse synchron halten, localStorage persistieren
  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => setDarkMode((prev) => !prev), []);

  // ── Ansicht (Aventurien / Klar) ────────────────────────────────────────────
  const [thema, setThemaState] = useState<Thema>(() => leseThema());

  useEffect(() => {
    setzeThema(thema);
  }, [thema]);

  const setzeAnsicht = useCallback((neues: Thema) => setThemaState(neues), []);
  const wechsleAnsicht = useCallback(() => setThemaState((aktuell) => naechstesThema(aktuell)), []);

  // ── Auth State ─────────────────────────────────────────────────────────────
  const applyUser = useCallback((authUser) => {
    setUser(authUser ?? null);
    setRole(resolveUserRole(authUser));
  }, []);

  useEffect(() => {
    if (!supabase) return;

    // Initialen Auth-State einmalig laden
    supabase.auth.getUser().then(({ data: { user: authUser } }) => {
      applyUser(authUser);
      setLoading(false);
    });

    // Auf Auth-Änderungen reagieren (Login, Logout, Token-Refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      applyUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [applyUser]);

  const value = {
    user,
    role,
    loading,
    darkMode,
    toggleDarkMode,
    thema,
    setzeAnsicht,
    wechsleAnsicht,
    isAuthenticated: Boolean(user),
    // isGM heißt »darf die Runde leiten« und entspricht public.is_admin().
    isGM: istSpielleitung(role),
    // Die Assistenz sieht den Meisterbereich, ändert dort aber nicht alles.
    isMeisterTeam: istMeisterTeam(role),
    isSuperAdmin: istSuperAdmin(role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth – Hook zum Zugriff auf den Auth-Context.
 * Wirft einen Fehler, wenn er außerhalb des Providers verwendet wird.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth muss innerhalb eines <AuthProvider> verwendet werden.');
  }
  return ctx;
}

export default AuthContext;
